import React, { createContext, useEffect, useReducer } from 'react';

// reducer - state management
import { LOGIN, LOGOUT, UPDATE_PROFILE } from 'contexts/auth-reducer/actions';
import authReducer from 'contexts/auth-reducer/auth';

// project imports
import Loader from 'components/Loader';
import axios from 'utils/axios';
import { AuthProps, JWTContextType } from 'types/auth';
import { clearToken, setToken, getRefreshToken, setRefreshToken, isDesktopClient } from 'utils/axios';

// constant
const initialState: AuthProps = {
  isLoggedIn: false,
  isInitialized: false,
  user: null
};

const setSession = (serviceToken?: string | null) => {
  if (serviceToken) {
    localStorage.setItem('serviceToken', serviceToken);
  } else {
    localStorage.removeItem('serviceToken');
  }
};

// ==============================|| JWT CONTEXT & PROVIDER ||============================== //

const JWTContext = createContext<JWTContextType | null>(null);

export const JWTProvider = ({ children }: { children: React.ReactElement }) => {
  const [state, dispatch] = useReducer(authReducer, initialState);

  useEffect(() => {
    const init = async () => {
      try {
        // Se já havia token, ele será verificado no backend via /auth/me
        let serviceToken = window.localStorage.getItem('serviceToken');

        // Electron: sem access token mas com refresh persistido -> restaura a sessão
        if (!serviceToken && isDesktopClient() && getRefreshToken()) {
          try {
            const { data } = await axios.post('/auth/refresh', null, {
              headers: {
                'X-Requested-With': 'XMLHttpRequest',
                'x-client-platform': 'electron',
                'x-refresh-token': getRefreshToken() as string
              }
            });
            if (data?.data?.accessToken) {
              setToken(data.data.accessToken);
              if (data?.data?.refreshToken) setRefreshToken(data.data.refreshToken);
              serviceToken = data.data.accessToken;
            }
          } catch {
            setRefreshToken(null);
          }
        }

        if (serviceToken) {
          // não decodifique para decidir — pergunte ao backend
          const response = await axios.get('/auth/me');

          // Baseado nos dados fornecidos, a API retorna { data: {...}, expiresIn: {...} }
          const { data: userData, expiresIn } = response.data;

          dispatch({
            type: LOGIN,
            payload: { isLoggedIn: true, user: userData, expiresIn }
          });
        } else {
          dispatch({ type: LOGOUT });
        }
      } catch (err: any) {
        console.error(err);

        // O interceptor do axios já cuida do redirecionamento para 401
        // Aqui apenas limpamos o estado local
        clearToken();
        dispatch({ type: LOGOUT });
      }
    };
    init();
  }, []);

  const login = async (email: string, password: string) => {
    try {
      // chama seu backend real
      const response = await axios.post('/auth/login', { email, password });

      // A API retorna { serviceToken, user, expiresIn }
      const { serviceToken, user: userData, expiresIn } = response.data;

      // Garante que o userData existe e tem a estrutura correta
      if (!userData) {
        throw new Error('Dados do usuário não encontrados na resposta');
      }

      setSession(serviceToken);
      // Electron: persiste o refresh token devolvido no corpo para manter a sessão
      if (isDesktopClient() && response.data?.refreshToken) {
        setRefreshToken(response.data.refreshToken);
      }
      dispatch({
        type: LOGIN,
        payload: { isLoggedIn: true, user: userData, expiresIn }
      });
    } catch (error: any) {
      // Network Error = servidor inacessível (não está rodando, URL errada, CORS, etc.)
      const isNetworkError =
        error.code === 'ERR_NETWORK' ||
        error.message === 'Network Error' ||
        (error.message && String(error.message).toLowerCase().includes('network error'));
      const errorMessage = isNetworkError
        ? 'Não foi possível conectar ao servidor. Verifique se a API está rodando e se a URL está correta (arquivo .env: VITE_APP_API_URL).'
        : error.response?.data?.message || error.message || 'Erro ao fazer login';
      throw new Error(errorMessage);
    }
  };

  // register/resetPassword podem continuar mock ou apontar para seus endpoints reais, se existirem
  const register = async () => Promise.resolve();

  const logout = async () => {
    try {
      await axios.post('/auth/logout'); // invalida refresh no servidor
    } catch {}
    setSession(null);
    setRefreshToken(null);
    dispatch({ type: LOGOUT });
  };

  const resetPassword = async (email: string) => {
    try {
      const response = await axios.post('/auth/forgot-password', { email });
      return response.data;
    } catch (error: any) {
      // Extrai a mensagem de erro da API ou usa uma mensagem padrão
      const errorMessage = error.response?.data?.message || error.message || 'Erro ao enviar e-mail de redefinição de senha';
      throw new Error(errorMessage);
    }
  };

  const updateProfile = (userData: any) => {
    dispatch({
      type: UPDATE_PROFILE,
      payload: { isLoggedIn: true, user: userData }
    });
  };

  const refreshUser = async () => {
    try {
      const response = await axios.get('/auth/me');
      const { data: userData, expiresIn } = response.data;

      dispatch({
        type: LOGIN,
        payload: { isLoggedIn: true, user: userData, expiresIn }
      });
    } catch (error: any) {
      console.error('Erro ao atualizar dados do usuário:', error);
      // Não faz logout em caso de erro, apenas loga o erro
    }
  };

  // Só mostra o loader se ainda não foi inicializado
  if (!state.isInitialized) {
    return <Loader />;
  }

  return <JWTContext value={{ ...state, login, logout, register, resetPassword, updateProfile, refreshUser }}>{children}</JWTContext>;
};

export default JWTContext;
